import React, { useCallback, useContext, useState } from 'react';
import { __RouterContext } from 'react-router';

import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import CircleInfoFill from '@gravity-ui/icons/CircleInfoFill';
import { Button, Card, Modal, Tooltip } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { granetApi } from 'client/api';
import { GrammarPageIntentModule } from 'client/blocks-redesign/grammar/GrammarPageIntentModule/GrammarPageIntentModule';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { ModalWithConfirmation } from 'client/blocks-redesign/ModalWithConfirmation/ModalWithConfirmation';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { GrammarPageContext, useGrammarPageContext } from 'client/context/grammarPageContext';
import { SettingsPageContext, useSettingsPageContext } from 'client/context/settingsPageContext';
import { useLoadingData } from 'client/hooks/useLoadingData';
import { emptyGrammarIntentSettings, GrammarIntentShort, IntentStatus } from 'client/model/grammar';
import { preventEsc } from 'client/utils/events';
import * as commonKeyset from 'i18n/Common';
import * as keyset from 'i18n/IntentsPage';

import styles from './GrammarPageIntent.module.scss';

const t = i18n(keyset);
const tCommon = i18n(commonKeyset);

interface GrammarPageIntentPopupProps {
    onClose: () => void;
    intent: GrammarIntentShort;
}

interface GrammarPageIntentProps {
    intent: GrammarIntentShort;
}

const GrammarPageIntentPopup: React.FC<GrammarPageIntentPopupProps> = ({ onClose, intent }) => {
    const [isChanged, setIsChanged] = useState(false);
    const [confirmIsOpen, setConfirmIsOpen] = useState(false);
    const [intentSettings, setIntentSettings] = useState(emptyGrammarIntentSettings);

    const skillContext = useContext(CurrentAliceAppContext)!;
    const grammarPageContext = useGrammarPageContext();
    const settingsPageContext = useSettingsPageContext();
    const routerContext = useContext(__RouterContext);

    const { removeIntentFromView } = grammarPageContext;
    const { isDraft, isEditable } = settingsPageContext;

    const onIntentDelete = () => {
        removeIntentFromView(intent.id);
        onClose();
    };

    const { loadingMeta } = useLoadingData({
        loader: async () => {
            const settings = await granetApi.getGrammarIntent({
                skillId: skillContext.aliceApp.id,
                channel: skillContext.aliceApp?.channel,
                intentId: intent.id,
                isDraft,
            });

            setIntentSettings(settings);
        },
    });
    const { state } = loadingMeta;

    const skill = skillContext.aliceApp!;
    const customEntities = isDraft ? skill.draft.customEntities : skill.customEntities;

    const customEntitiesNames = (customEntities.match(/^entity\s+\w+(?=:)/) ?? []).map((match) =>
        match.replace(/^entity\s*/, ''),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onModalClose = useCallback(
        preventEsc(() => {
            isChanged ? setConfirmIsOpen(true) : onClose();
        }),
        [isChanged],
    );

    const handleConfirmClose = () => {
        setConfirmIsOpen(false);
        onClose();
    };

    return (
        <Modal className={styles.grammarPageIntent__modal} open onOutsideClick={onModalClose}>
            <div className={styles.grammarPageIntent__modalContent}>
                {state === 'loading' ? (
                    <PageLoader />
                ) : (
                    <GrammarPageContext.Provider value={grammarPageContext}>
                        <SettingsPageContext.Provider value={settingsPageContext}>
                            <CurrentAliceAppContext.Provider value={skillContext}>
                                <__RouterContext.Provider value={routerContext}>
                                    <GrammarPageIntentModule
                                        intent={intent}
                                        onDelete={onIntentDelete}
                                        readOnly={!isEditable}
                                        onClose={onModalClose}
                                        customEntitiesNames={customEntitiesNames}
                                        isChanged={isChanged}
                                        setIsChanged={setIsChanged}
                                        intentSettings={intentSettings}
                                    />
                                </__RouterContext.Provider>
                            </CurrentAliceAppContext.Provider>
                        </SettingsPageContext.Provider>
                    </GrammarPageContext.Provider>
                )}
            </div>
            {confirmIsOpen && (
                <ModalWithConfirmation
                    title={t('warning-title')}
                    text={t('warning-message')}
                    confirmButtonLabel={tCommon('yes')}
                    cancelButtonLabel={tCommon('no')}
                    onClose={() => setConfirmIsOpen(false)}
                    onConfirm={handleConfirmClose}
                />
            )}
        </Modal>
    );
};

const getHint = (status: IntentStatus) => {
    switch (status) {
        case IntentStatus.INVALID_GRAMMAR:
            return (
                <Tooltip content={t('tooltip-invalid-intent')} openDelay={100} placement="left">
                    <div className={styles.grammarPageIntent__tooltipContainer}>
                        <CircleInfoFill className={styles.grammarPageIntent__icon_invalid} />
                    </div>
                </Tooltip>
            );
        case IntentStatus.NEW:
            return (
                <Tooltip content={t('tooltip-new')} openDelay={100} placement="left">
                    <div className={styles.grammarPageIntent__tooltipContainer}>
                        <CircleInfoFill className={styles.grammarPageIntent__icon_new} />
                    </div>
                </Tooltip>
            );
        case IntentStatus.OK:
            return (
                <div className={styles.grammarPageIntent__tooltipContainer}>
                    <CircleCheckFill className={styles.grammarPageIntent__icon_ok} />
                </div>
            );
    }
};

export const GrammarPageIntent: React.FC<GrammarPageIntentProps> = ({ intent }) => {
    const [popupIsVisible, setPopupIsVisible] = useState(false);
    const { status } = intent;

    const handleOpen = () => setPopupIsVisible(true);
    const handleClose = useCallback(() => setPopupIsVisible(false), []);

    return (
        <>
            <Card className={styles.grammarPageIntent__container}>
                <Button
                    onClick={handleOpen}
                    className={styles.grammarPageIntent__buttonIntent}
                    view="flat"
                    width="max"
                    size="xl"
                >
                    {intent.humanReadableName || t('intent-no-name')}
                </Button>
                {getHint(status)}
            </Card>
            {popupIsVisible && <GrammarPageIntentPopup onClose={handleClose} intent={intent} />}
        </>
    );
};
